import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, RotateCcw, ExternalLink, ShieldCheck, Plus, Trash2, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { logger } from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_META = {
  brand_name:     { label: 'Brand name',        placeholder: 'PAWSOME VENTURES' },
  brand_tagline:  { label: 'Brand tagline',     placeholder: 'Making stress-free pet travel pawsible' },
  title:          { label: 'Main title',        placeholder: 'Benefits & Scope of Assistance' },
  subtitle:       { label: 'Subtitle',          placeholder: 'What using Pawsome Ventures actually earns you' },
  intro:          { label: 'Intro paragraph',   placeholder: 'International pet travel is complex…', multiline: true, rows: 3 },
  benefits_title: { label: 'Left column heading',  placeholder: 'Why work with us' },
  scope_title:    { label: 'Right column heading', placeholder: "What's included" },
  scope_imports_title: { label: 'Imports sub-heading', placeholder: 'Pet imports into Cayman' },
  scope_exports_title: { label: 'Exports sub-heading', placeholder: 'Pet exports from Cayman' },
  benefits_footnote: { label: 'Footnote under benefits', placeholder: 'We cannot expedite Cayman government processing…', multiline: true, rows: 2 },
  next_step_title: { label: 'Call-to-action title',  placeholder: 'To get an accurate quote and timeline' },
  next_step_body:  { label: 'Call-to-action body',   placeholder: 'Tell us where you are moving from…', multiline: true, rows: 3 },
  footer_website: { label: 'Website', placeholder: 'pawsomeventures.ky' },
  footer_email:   { label: 'Email',   placeholder: 'pawsome.ky@gmail.com' },
  footer_phone:   { label: 'Phone',   placeholder: '+1 345 916 2281' },
};

const SECTIONS = [
  { title: 'Header',   keys: ['brand_name', 'brand_tagline', 'title', 'subtitle'] },
  { title: 'Intro',    keys: ['intro'] },
  { title: 'Column headings + footnote', keys: ['benefits_title', 'scope_title', 'scope_imports_title', 'scope_exports_title', 'benefits_footnote'] },
  { title: 'Call to action', keys: ['next_step_title', 'next_step_body'] },
  { title: 'Footer',   keys: ['footer_website', 'footer_email', 'footer_phone'] },
];

const STATUS_META = {
  included: { label: 'Included', color: 'bg-primary text-white' },
  quoted_as_needed: { label: 'Quoted as needed', color: 'bg-teal-100 text-teal-800' },
};

export const BenefitsScopeFlyerEditor = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const auth = useMemo(() => ({ Authorization: `Basic ${btoa(':' + password)}` }), [password]);

  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings/benefits-scope-flyer`, { headers: auth });
      setContent(data.content);
    } catch (e) {
      logger.error('benefits flyer load', e);
      toast.error('Failed to load flyer content');
    } finally { setLoading(false); }
  };

  const setField = (k, v) => setContent((prev) => ({ ...prev, [k]: v }));

  // Benefits list
  const setBenefit = (i, key, v) => setContent((prev) => {
    const items = [...(prev.benefits || [])];
    items[i] = { ...items[i], [key]: v };
    return { ...prev, benefits: items };
  });
  const addBenefit = () => setContent((prev) => ({ ...prev, benefits: [...(prev.benefits || []), { title: '', body: '' }] }));
  const removeBenefit = (i) => setContent((prev) => ({ ...prev, benefits: prev.benefits.filter((_, idx) => idx !== i) }));

  // Scope items — two lists (imports + exports)
  const setScope = (listKey, i, key, v) => setContent((prev) => {
    const items = [...(prev[listKey] || [])];
    items[i] = { ...items[i], [key]: v };
    return { ...prev, [listKey]: items };
  });
  const addScope = (listKey) => setContent((prev) => ({ ...prev, [listKey]: [...(prev[listKey] || []), { label: '', status: 'included' }] }));
  const removeScope = (listKey, i) => setContent((prev) => ({ ...prev, [listKey]: prev[listKey].filter((_, idx) => idx !== i) }));
  const moveScope = (listKey, i, delta) => setContent((prev) => {
    const items = [...(prev[listKey] || [])];
    const j = i + delta;
    if (j < 0 || j >= items.length) return prev;
    [items[i], items[j]] = [items[j], items[i]];
    return { ...prev, [listKey]: items };
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings/benefits-scope-flyer`, content, { headers: auth });
      setContent(data.content);
      toast.success('Flyer saved — preview refreshed');
      setPreviewKey((k) => k + 1);
    } catch (e) {
      logger.error('benefits flyer save', e);
      toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm('Reset the flyer to the original wording? Your current edits will be discarded.')) return;
    try {
      const { data } = await axios.post(`${API}/admin/settings/benefits-scope-flyer/reset`, {}, { headers: auth });
      setContent(data.content);
      setPreviewKey((k) => k + 1);
      toast.success('Reverted to defaults');
    } catch (e) { toast.error('Reset failed'); }
  };

  const previewUrl = useMemo(
    () => `${process.env.REACT_APP_BACKEND_URL}/api/brand/benefits-scope-flyer.pdf?v=${previewKey}`,
    [previewKey],
  );

  if (loading || !content) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-foreground/60">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading flyer content…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="benefits-flyer-editor">
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-lg hover:bg-gray-100" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-base sm:text-xl font-bold text-primary">Benefits &amp; Scope Flyer</h1>
              <p className="text-xs text-foreground/60">Attach to enquiry replies · preview refreshes on save</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50" data-testid="benefits-open-pdf">
              <ExternalLink className="w-4 h-4" /> Open PDF
            </a>
            <Button variant="outline" onClick={resetAll} data-testid="benefits-reset">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
            </Button>
            <Button onClick={save} disabled={saving} data-testid="benefits-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form */}
        <div className="lg:col-span-3 space-y-6">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white border border-border rounded-xl p-5">
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary mb-3">{section.title}</h2>
              <div className="space-y-3">
                {section.keys.map((k) => {
                  const meta = FIELD_META[k];
                  if (!meta) return null;
                  return (
                    <div key={k}>
                      <Label>{meta.label}</Label>
                      {meta.multiline ? (
                        <textarea
                          value={content[k] || ''}
                          onChange={(e) => setField(k, e.target.value)}
                          rows={meta.rows || 3}
                          data-testid={`field-${k}`}
                          className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder={meta.placeholder}
                        />
                      ) : (
                        <Input
                          value={content[k] || ''}
                          onChange={(e) => setField(k, e.target.value)}
                          placeholder={meta.placeholder}
                          data-testid={`field-${k}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <section className="border-y border-border py-4" data-testid="benefits-scope-options">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-primary">Scope options</h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(STATUS_META).map(([key, option]) => <span key={key} data-testid={`benefits-option-${key}`} className={`rounded px-3 py-1 text-xs font-semibold ${option.color}`}>{option.label}</span>)}
            </div>
          </section>

          {/* Benefits cards */}
          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary">Benefit cards</h2>
              <Button variant="outline" size="sm" onClick={addBenefit} data-testid="add-benefit">
                <Plus className="w-4 h-4 mr-1" /> Add benefit
              </Button>
            </div>
            <p className="text-xs text-foreground/50 mb-3">Up to 4 cards fit on the flyer.</p>
            <div className="space-y-3">
              {(content.benefits || []).map((b, i) => (
                <div key={i} className="border border-border rounded-lg p-3 space-y-2 bg-gray-50/50" data-testid={`benefit-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-foreground/60 uppercase">Card {i + 1}</span>
                    <button onClick={() => removeBenefit(i)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`benefit-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Input
                    value={b.title || ''}
                    placeholder="Benefit title"
                    onChange={(e) => setBenefit(i, 'title', e.target.value)}
                    data-testid={`benefit-title-${i}`}
                  />
                  <textarea
                    value={b.body || ''}
                    placeholder="Supporting sentence"
                    onChange={(e) => setBenefit(i, 'body', e.target.value)}
                    rows={2}
                    data-testid={`benefit-body-${i}`}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Scope rows — imports + exports */}
          {[
            { key: 'scope_imports', label: 'Imports scope', hint: 'Pet imports into Cayman.' },
            { key: 'scope_exports', label: 'Exports scope', hint: 'Pet exports from Cayman.' },
          ].map(({ key: listKey, label, hint }) => (
            <div key={listKey} className="bg-white border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wide text-primary">{label}</h2>
                <Button variant="outline" size="sm" onClick={() => addScope(listKey)} data-testid={`add-${listKey}`}>
                  <Plus className="w-4 h-4 mr-1" /> Add row
                </Button>
              </div>
              <p className="text-xs text-foreground/50 mb-3">{hint} Up to 8 rows fit. Pick a status chip per row.</p>
              <div className="space-y-2">
                {(content[listKey] || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-center sm:grid-cols-[minmax(0,1fr)_165px_auto]" data-testid={`${listKey}-row-${i}`}>
                    <Input
                      value={s.label || ''}
                      placeholder="Service label"
                      onChange={(e) => setScope(listKey, i, 'label', e.target.value)}
                      data-testid={`${listKey}-label-${i}`}
                      className="col-span-2 min-w-0 sm:col-span-1"
                    />
                    <select
                      value={s.status || 'included'}
                      onChange={(e) => setScope(listKey, i, 'status', e.target.value)}
                      data-testid={`${listKey}-status-${i}`}
                      className="min-w-0 w-full px-2 py-2 text-xs border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                    >
                      {Object.entries(STATUS_META).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => moveScope(listKey, i, -1)} disabled={i === 0} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" title="Move up" aria-label="Move scope item up" data-testid={`${listKey}-up-${i}`}>↑</button>
                      <button onClick={() => moveScope(listKey, i, 1)} disabled={i === (content[listKey] || []).length - 1} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" title="Move down" aria-label="Move scope item down" data-testid={`${listKey}-down-${i}`}>↓</button>
                      <button onClick={() => removeScope(listKey, i)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Remove item" aria-label="Remove scope item" data-testid={`${listKey}-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Live preview */}
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-white border border-border rounded-xl p-3 sticky top-24">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground/60 uppercase">Live preview</span>
              <span className="text-[11px] text-foreground/40">Refreshes on Save</span>
            </div>
            <div className="aspect-[210/297] bg-gray-100 rounded-lg overflow-hidden border border-border">
              <iframe
                key={previewKey}
                title="Benefits flyer preview"
                src={previewUrl}
                className="w-full h-full"
                data-testid="benefits-preview-iframe"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenefitsScopeFlyerEditor;
